import i18n from "@/i18n";
/**
 * EmployeeEditSheet — Prompt 12L-2C §6-§14.
 *
 * Manager-facing sheet used to rename an existing Employee. Only
 * `first_name` and `last_name` are editable; every other business
 * field lives on the effective-dated Employment / Employment Terms
 * timeline and remains OUT OF SCOPE for this block.
 *
 * Presentational / interaction only:
 *   - Reads canonical initial values from `employee`.
 *   - Emits a Save intent through `onSave(body)` where `body` contains
 *     only the fields that actually changed (never `restaurant_id`,
 *     never `company_id`, never `employee_id`, never role / contract /
 *     employment / terms fields).
 *   - Save is disabled while `pending` is true (owner-supplied), when
 *     the form is unchanged, or when a supplied name is blank.
 *   - Cancel closes without emitting anything.
 *
 * The PATCH itself is owned by `WorkforceTeam` (Prompt 12L-2C §3/§10).
 */
import { useEffect, useMemo, useState } from "react";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { EmployeeNumberInput } from "./EmployeeNumberInput";

// ── Pure helpers exported for tests ────────────────────────────
export function trimName(v) {
    return typeof v === "string" ? v.trim() : "";
}

/**
 * Build the PATCH body containing ONLY the fields that actually
 * changed relative to the canonical initial values (Prompt 12L-2C §9).
 * Returns `null` when the form is unchanged so the caller can leave
 * Save disabled without emitting an intent.
 */
export function buildEmployeeEditPayload({
    initialFirstName, initialLastName, initialWorkerType,
    firstName, lastName, workerType,
    initialEmployeeNumber, employeeNumber,
}) {
    const nextFirst = trimName(firstName);
    const nextLast = trimName(lastName);
    const body = {};
    if (nextFirst !== initialFirstName) body.first_name = nextFirst;
    if (nextLast !== initialLastName) body.last_name = nextLast;
    // Prompt 20A §4 · §18 — worker classification is an additive
    // mutation. Only include when it actually changed.
    if (workerType && workerType !== initialWorkerType) {
        body.worker_type = workerType;
    }
    // PAYROLL-FINAL-B2 — only when changed; blank clears (null).
    if (employeeNumber !== undefined) {
        const next = trimName(employeeNumber);
        if (next !== (initialEmployeeNumber ?? "")) {
            body.employee_number = next || null;
        }
    }
    return Object.keys(body).length === 0 ? null : body;
}

/**
 * Frontend save-eligibility predicate (Prompt 12L-2C §8/§9). The
 * backend remains the final authority — this only prevents obviously
 * invalid submits (blank / whitespace-only when supplied) and blocks
 * unchanged forms.
 */
export function isEditSubmittable({
    initialFirstName, initialLastName, initialWorkerType,
    firstName, lastName, workerType, pending,
    initialEmployeeNumber, employeeNumber,
}) {
    if (pending) return false;
    // Both effective values equal the canonical baseline → nothing to
    // save.
    const body = buildEmployeeEditPayload({
        initialFirstName, initialLastName, initialWorkerType,
        firstName, lastName, workerType,
        initialEmployeeNumber, employeeNumber,
    });
    if (!body) return false;
    // Blank-when-supplied: reject a submit that would zero-out a name.
    if ("first_name" in body && body.first_name === "") return false;
    if ("last_name" in body && body.last_name === "") return false;
    return true;
}

export default function EmployeeEditSheet({
    open, onOpenChange, employee, pending, onSave,
}) {
    const initialFirstName = employee?.first_name ?? "";
    const initialLastName = employee?.last_name ?? "";
    const initialWorkerType = employee?.worker_type ?? "DIRECT";
    const initialEmployeeNumber = employee?.employee_number ?? "";

    const [firstName, setFirstName] = useState(initialFirstName);
    const [lastName, setLastName] = useState(initialLastName);
    const [workerType, setWorkerType] = useState(initialWorkerType);
    const [employeeNumber, setEmployeeNumber] = useState(
        initialEmployeeNumber);

    // Prompt 12L-2C §7 — every time the sheet opens (or the canonical
    // employee changes while it is open) reset the local inputs to
    // the CURRENT canonical names. Reopening after a successful
    // reload must show the newest canonical values.
    useEffect(() => {
        if (open) {
            setFirstName(initialFirstName);
            setLastName(initialLastName);
            setWorkerType(initialWorkerType);
            setEmployeeNumber(initialEmployeeNumber);
        }
    }, [open, initialFirstName, initialLastName, initialWorkerType,
        initialEmployeeNumber]);

    const canSubmit = useMemo(() => isEditSubmittable({
        initialFirstName, initialLastName, initialWorkerType,
        firstName, lastName, workerType, pending,
        initialEmployeeNumber, employeeNumber,
    }), [initialFirstName, initialLastName, initialWorkerType,
         firstName, lastName, workerType, pending,
         initialEmployeeNumber, employeeNumber]);

    const submit = () => {
        if (!canSubmit) return;
        const body = buildEmployeeEditPayload({
            initialFirstName, initialLastName, initialWorkerType,
            firstName, lastName, workerType,
            initialEmployeeNumber, employeeNumber,
        });
        if (!body) return;
        // Owner (WorkforceTeam) performs the PATCH and closes the
        // sheet on success (Prompt 12L-2C §11/§12).
        onSave?.(body);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                data-testid="employee-edit-sheet"
                className="w-full sm:max-w-md overflow-y-auto"
            >
                <SheetHeader>
                    <SheetTitle data-testid="employee-edit-title">
                        {i18n.t("wf.employeeEditSheet.editEmployee", "Edit Employee")}
                    </SheetTitle>
                </SheetHeader>

                <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block text-sm">
                            <span className="text-neutral-700">
                                {i18n.t("wf.employeeEditSheet.firstName", "First Name")}
                            </span>
                            <Input
                                data-testid="employee-edit-first-name"
                                value={firstName}
                                onChange={(e) =>
                                    setFirstName(e.target.value)}
                                disabled={pending}
                            />
                        </label>
                        <label className="block text-sm">
                            <span className="text-neutral-700">
                                {i18n.t("wf.employeeEditSheet.lastName", "Last Name")}
                            </span>
                            <Input
                                data-testid="employee-edit-last-name"
                                value={lastName}
                                onChange={(e) =>
                                    setLastName(e.target.value)}
                                disabled={pending}
                            />
                        </label>
                    </div>

                    <EmployeeNumberInput
                        testId="employee-edit-number"
                        value={employeeNumber}
                        onChange={setEmployeeNumber}
                        disabled={pending}
                    />

                    {/* Prompt 20A §4 · §18 — Worker type. Manager can
                        toggle between Regular employee (DIRECT) and
                        Extra / temporary (EXTRA) through the same
                        Edit sheet. */}
                    <div className="text-sm">
                        <span className="text-neutral-700">
                            {i18n.t("wf.employeeEditSheet.workerType", "Worker type")}
                        </span>
                        <Select
                            value={workerType}
                            onValueChange={(v) => setWorkerType(v)}
                            disabled={pending}
                        >
                            <SelectTrigger
                                data-testid="employee-edit-worker-type">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="DIRECT"
                                             data-testid="edit-opt-direct">
                                    {i18n.t("wf.employeeEditSheet.regularEmployee", "Regular employee")}
                                </SelectItem>
                                <SelectItem value="EXTRA"
                                             data-testid="edit-opt-extra">
                                    {i18n.t("wf.employeeEditSheet.extraTemporary", "Extra / temporary")}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2">
                        <Button
                            variant="outline"
                            data-testid="employee-edit-cancel"
                            onClick={() => onOpenChange?.(false)}
                            disabled={pending}
                        >
                            {i18n.t("common.cancel", "Cancel")}
                        </Button>
                        <Button
                            data-testid="employee-edit-save"
                            onClick={submit}
                            disabled={!canSubmit}
                        >
                            {pending ? i18n.t("common.saving", "Saving…") : i18n.t("common.save", "Save")}
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
